import { create } from 'zustand';
import type { Draft, EmailAnalysis, ToneType } from '../types/index';

export type AttachmentStatus = 'idle' | 'loading' | 'loaded' | 'error' | 'skipped';

export interface SkippedAttachment {
  name: string;
  reason: string;
}

interface DraftState {
  // Current draft
  draft: Draft | null;
  analysis: EmailAnalysis | null;
  summary: string | null;

  // Generation state
  isGenerating: boolean;
  error: string | null;

  // Attachment state
  attachmentStatus: AttachmentStatus;
  attachmentCount: number;
  skippedAttachments: SkippedAttachment[];
  attachmentWarning: string | null;

  // Selected tone (overrides user default)
  selectedTone: ToneType | null;

  // Actions
  setDraft: (draft: Draft | null) => void;
  setAnalysis: (analysis: EmailAnalysis | null) => void;
  setSummary: (summary: string | null) => void;
  setIsGenerating: (isGenerating: boolean) => void;
  setError: (error: string | null) => void;
  setAttachmentStatus: (status: AttachmentStatus) => void;
  setAttachmentCount: (count: number) => void;
  setSkippedAttachments: (skipped: SkippedAttachment[]) => void;
  setAttachmentWarning: (warning: string | null) => void;
  setSelectedTone: (tone: ToneType | null) => void;
  clearDraft: () => void;
  reset: () => void;
}

const initialState = {
  draft: null,
  analysis: null,
  summary: null,
  isGenerating: false,
  error: null,
  attachmentStatus: 'idle' as AttachmentStatus,
  attachmentCount: 0,
  skippedAttachments: [] as SkippedAttachment[],
  attachmentWarning: null,
  selectedTone: null,
};

export const useDraftStore = create<DraftState>((set) => ({
  ...initialState,

  setDraft: (draft) => set({ draft }),
  setAnalysis: (analysis) => set({ analysis }),
  setSummary: (summary) => set({ summary }),
  setIsGenerating: (isGenerating) => set({ isGenerating }),
  setError: (error) => set({ error }),
  setAttachmentStatus: (status) => set({ attachmentStatus: status }),
  setAttachmentCount: (count) => set({ attachmentCount: count }),
  setSkippedAttachments: (skipped) => set({ skippedAttachments: skipped }),
  setAttachmentWarning: (warning) => set({ attachmentWarning: warning }),
  setSelectedTone: (tone) => set({ selectedTone: tone }),

  clearDraft: () =>
    set({
      draft: null,
      analysis: null,
      summary: null,
      error: null,
      attachmentStatus: 'idle',
      attachmentCount: 0,
      skippedAttachments: [],
      attachmentWarning: null,
    }),

  reset: () => set(initialState),
}));
